import { readSelectedWorkspaceId } from '@/lib/api/workspaceScopedEndpoints';
import { apiClient } from '../client';
import type { ApiResponse } from '../config';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'AGENT' | 'USER' | 'RIDER' | string;
  status: 'active' | 'inactive' | string;
  preferences?: { menu_permissions?: string[] | null; [key: string]: any };
  avatar_url?: string;
  phone?: string;
  company?: string;
  is_organization_owner?: boolean;
  is_super_admin?: boolean;
  organization_id?: string;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
  has_all_conversations_access?: boolean; // If true, agent sees all conversations (snake_case from backend)
  hasAllConversationsAccess?: boolean;    // CamelCase convenience for frontend
  is_online?: boolean;
  last_seen?: string;
  assigned_accounts?: {
    id: string;
    name: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
  is_active?: boolean;
  last_login_at?: string;
}

export interface TeamMemberWithAccess extends TeamMember {
  has_all_conversations_access?: boolean;
}

export interface TeamStats {
  total_members: number;
  active_members: number;
  inactive_members: number;
  role_breakdown: { ADMIN: number; AGENT: number; USER: number; RIDER: number };
  seat_limit?: number | null;
  role_limits?: { admin: number | null; user: number | null; agent: number | null };
  role_usage?: { admin: number; user: number; agent: number };
}

export interface InviteMemberData {
  email: string;
  name: string;
  role: 'ADMIN' | 'AGENT' | 'USER' | 'RIDER';
  businessAccountIds?: string[];
  assignments?: {
    business_account_id: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
  password?: string; // Optional - if not provided, random password will be generated
  hasAllConversationsAccess?: boolean; // Optional - if true, agent sees all conversations
  phone?: string; // For RIDER role: stores the rider's registration/vehicle number
}

export interface UpdateMemberData {
  id: string;
  name?: string;
  email?: string;
  role?: 'ADMIN' | 'AGENT' | 'USER' | string;
  status?: 'active' | 'inactive' | string;
  phone?: string;
  company?: string;
  hasAllConversationsAccess?: boolean;
  menuPermissions?: string[] | null;
  assignments?: {
    business_account_id: string;
    permissions?: {
      has_all_conversations_access?: boolean;
    };
  }[];
}

type TeamPagesResult = { ok: boolean; rows: TeamMember[]; error?: string };

/*
 * The unfiltered member list is read page by page by two callers at app start
 * (the chat context and the agent directory). A request made while the same
 * workspace's read is still IN FLIGHT shares it; once it settles, the next
 * request reads fresh — so a reload after a team change never sees old rows.
 */
let sharedTeamPages: { workspaceId: string | null; promise: Promise<TeamPagesResult> } | null = null;

async function readTeamPages(params: { role?: string; status?: string; search?: string; is_online?: boolean }): Promise<TeamPagesResult> {
  const PAGE = 100;
  const byId = new Map<string, TeamMember>();
  for (let page = 0; page < 50; page += 1) {
    const res = (await TeamService.getTeamMembers({ ...params, limit: PAGE, offset: page * PAGE })) as unknown as
      { success?: boolean; data?: TeamMember[] | { data?: TeamMember[] }; error?: string } | undefined;
    const rows = Array.isArray(res?.data)
      ? res!.data as TeamMember[]
      : Array.isArray((res?.data as { data?: TeamMember[] } | undefined)?.data)
        ? (res!.data as { data: TeamMember[] }).data
        : null;
    if (!res || res.success === false || !rows) {
      return { ok: false, rows: [...byId.values()], error: res?.error || 'Failed to load team members' };
    }
    for (const m of rows) if (m?.id && !byId.has(m.id)) byId.set(m.id, m);
    if (rows.length < PAGE) break;
  }
  return { ok: true, rows: [...byId.values()] };
}

function teamPages(params: { role?: string; status?: string; search?: string; is_online?: boolean }): Promise<TeamPagesResult> {
  const unfiltered = Object.values(params).every((v) => v === undefined || v === '');
  if (!unfiltered) return readTeamPages(params);
  const workspaceId = readSelectedWorkspaceId();
  if (sharedTeamPages && sharedTeamPages.workspaceId === workspaceId) return sharedTeamPages.promise;
  const promise = readTeamPages(params);
  sharedTeamPages = { workspaceId, promise };
  const release = () => { if (sharedTeamPages?.promise === promise) sharedTeamPages = null; };
  promise.then(release, release);
  return promise;
}

const inFlightMemberReads = new Map<string, Promise<ApiResponse<{ success: boolean; data: TeamMember[] }>>>();

export const TeamService = {
  async getTeamMembers(params: { role?: string; status?: string; search?: string; limit?: number; offset?: number; is_online?: boolean } = {}) {
    if (params.is_online !== undefined) {
      // Live presence: always a fresh read.
      return apiClient.get<{ success: boolean; data: TeamMember[] }>(`/team/members`, { params: { ...params, _t: Date.now() } });
    }
    /*
      The chat panel and the agent directory both ask for the same page at app start. A call
      made while an identical one (same params, same workspace) is still in flight shares it;
      once it settles the next call reads fresh, so a team change is never hidden.
    */
    const key = `${readSelectedWorkspaceId() ?? ''}|${JSON.stringify(params)}`;
    const pending = inFlightMemberReads.get(key);
    if (pending) return pending;
    const promise = apiClient.get<{ success: boolean; data: TeamMember[] }>(`/team/members`, { params });
    inFlightMemberReads.set(key, promise);
    const release = () => { if (inFlightMemberReads.get(key) === promise) inFlightMemberReads.delete(key); };
    promise.then(release, release);
    return promise;
  },

  async getAllTeamMembers(params: { role?: string; status?: string; search?: string } = {}) {
    // Same rows as before: whatever was read before any failure.
    const { rows } = await teamPages(params);
    return rows.slice(); // own copy: the read may be shared with another caller
  },

  async getTeamStats() {
    return apiClient.get<{ success: boolean; data: TeamStats }>(`/team/stats`);
  },

  async inviteMember(payload: InviteMemberData) {
    return apiClient.post(`/team/invite`, payload);
  },

  async updateMember(
    id: string,
    payload: Partial<
      Pick<
        TeamMember,
        'name' | 'email' | 'role' | 'status' | 'phone' | 'company' | 'hasAllConversationsAccess'
      >
    > & { assignments?: UpdateMemberData['assignments']; menuPermissions?: string[] | null }
  ) {
    return apiClient.put(`/team/members/${id}`, payload);
  },

  async removeMember(id: string) {
    return apiClient.delete(`/team/members/${id}`);
  },

  async changeUserPassword(id: string, newPassword: string) {
    return apiClient.put(`/team/members/${id}/password`, { newPassword });
  },
};
